import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Trash2, Download, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import AxiosClient from '@/api/client';

const API_BASE_URL = import.meta.env.VITE_BACKEND_DOMAIN || 'http://localhost:8000';

interface MediaFile {
  id: string;
  media_type: 'photo' | 'audio';
  file_name: string;
  file_path: string;
  file_size: number;
  created_at: string;
}

interface MediaViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  media: MediaFile[];
  onDelete: (mediaId: string) => void;
  isDeleting: boolean;
  touchpointName: string;
  isBlocked?: boolean; // When true, prevents interaction (e.g., when confirmation dialog is open)
  isReadOnly?: boolean; // When true, hides delete button (for managers)
}

export function MediaViewerModal({
  isOpen,
  onClose,
  media,
  onDelete,
  isDeleting,
  touchpointName,
  isBlocked = false,
  isReadOnly = false,
}: MediaViewerModalProps) {
  const { t } = useTranslation();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const images = media.filter(m => m.media_type === 'photo');
  const audios = media.filter(m => m.media_type === 'audio');

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const downloadMedia = async (mediaId: string, filePath: string, filename: string) => {
    setDownloadingId(mediaId);
    try {
      // Extract type and filename from path
      const pathParts = filePath.split('/');
      const folderIndex = pathParts.findIndex(part => part.includes('assessment_'));
      const type = pathParts[folderIndex];
      const filenamePart = pathParts[folderIndex + 1];
      
      // Use Laravel download route with /download suffix to prevent CDN from treating as static file
      const endpoint = `/api/download-media/${type}/${filenamePart}/download`;
      
      // Set global flag to exempt download errors from screenshot detection
      window.__isDownloadingMedia = true;
      
      try {
        // Create AxiosClient with token support (SAME AS PDF)
        const client = new AxiosClient({
          endpoint: endpoint,
          contentType: 'application/json',
          isToken: true,
          baseURL: API_BASE_URL,
        });

        // Make request using AxiosClient instance (SAME AS PDF)
        const response = await client.instance.get(endpoint, {
          responseType: 'blob',
          headers: {
            Accept: '*/*',
          },
        });

        // Download file (EXACT SAME AS PDF)
        const blob = new Blob([response.data]);
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        // Cleanup (EXACT SAME AS PDF)
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
        
      } finally {
        // Clear flag
        setTimeout(() => {
          window.__isDownloadingMedia = false;
        }, 500);
      }
      
    } catch (error) {
      console.error('Download failed:', error);
      window.__isDownloadingMedia = false;
    } finally {
      setDownloadingId(null);
    }
  };

  const getMediaUrl = (filePath: string): string => {
    // Use environment variable for backend domain
    const backendDomain = import.meta.env.VITE_BACKEND_DOMAIN;
    // file_path already includes path from backend
    return `${backendDomain}${filePath}`;
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className={`w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto z-50 p-3 sm:p-4 md:p-6 ${isBlocked ? 'pointer-events-none' : ''}`}
        >
          <DialogHeader>
            <DialogTitle className="flex flex-col sm:flex-row items-start sm:items-center gap-2 text-sm sm:text-base md:text-lg">
              <span className="break-words leading-tight">
                {t('assessments.perform.mediaFiles', 'Media Files')} -{' '}
                {touchpointName}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 sm:space-y-6">
            {/* Images Section */}
            {images.length > 0 && (
              <div>
                <h3 className="text-sm sm:text-base md:text-lg font-semibold mb-2 sm:mb-3 flex items-center gap-2 flex-wrap">
                  <span>📷 {t('assessments.perform.images', 'Images')}</span>
                  <Badge variant="secondary" className="text-xs">{images.length}/24</Badge>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {images.map(image => (
                    <Card key={image.id} className="p-2 sm:p-3">
                      <div className="space-y-2">
                        <img
                          src={getMediaUrl(image.file_path)}
                          alt={image.file_name}
                          className="w-full h-36 sm:h-40 md:h-48 object-cover rounded-md"
                        />
                        <div
                          className="text-xs text-gray-600 truncate"
                          title={image.file_name}
                        >
                          {image.file_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatFileSize(image.file_size)}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-xs h-8 sm:h-9"
                            onClick={() =>
                              downloadMedia(
                                image.id,
                                image.file_path,
                                image.file_name || `image-${images.indexOf(image) + 1}.jpg`
                              )
                            }
                            disabled={downloadingId === image.id}
                          >
                            {downloadingId === image.id ? (
                              <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin sm:mr-1" />
                            ) : (
                              <Download className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                            )}
                            <span className="hidden sm:inline">{t('common.download', 'Download')}</span>
                          </Button>
                          {!isReadOnly && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => onDelete(image.id)}
                              disabled={isDeleting}
                              className="px-3 sm:px-4 h-8 sm:h-9"
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Audio Section */}
            {audios.length > 0 && (
              <div>
                <h3 className="text-sm sm:text-base md:text-lg font-semibold mb-2 sm:mb-3 flex items-center gap-2 flex-wrap">
                  <span>🎤 {t('assessments.perform.audios', 'Audios')}</span>
                  <Badge variant="secondary" className="text-xs">{audios.length}/5</Badge>
                </h3>
                <div className="space-y-2 sm:space-y-3">
                  {audios.map(audio => (
                    <Card key={audio.id} className="p-3 sm:p-4">
                      <div className="space-y-3">
                        <div className="flex flex-col gap-2">
                          <div
                            className="text-xs sm:text-sm font-medium truncate"
                            title={audio.file_name}
                          >
                            {audio.file_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatFileSize(audio.file_size)} •{' '}
                            {new Date(audio.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <audio
                          controls
                          className="w-full h-8 sm:h-10"
                          src={getMediaUrl(audio.file_path)}
                        >
                          {t(
                            'assessments.perform.audioNotSupported',
                            'Your browser does not support audio playback'
                          )}
                        </audio>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-xs h-8 sm:h-9"
                            onClick={() =>
                              downloadMedia(
                                audio.id,
                                audio.file_path,
                                audio.file_name || `audio-${audios.indexOf(audio) + 1}.mp3`
                              )
                            }
                            disabled={downloadingId === audio.id}
                          >
                            {downloadingId === audio.id ? (
                              <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin sm:mr-1" />
                            ) : (
                              <Download className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                            )}
                            <span className="hidden sm:inline">{t('common.download', 'Download')}</span>
                          </Button>
                          {!isReadOnly && (
                            <Button
                              variant="destructive"
                              size="sm"
                              className="flex-1 text-xs h-8 sm:h-9"
                              onClick={() => onDelete(audio.id)}
                              disabled={isDeleting}
                            >
                              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                              <span className="hidden sm:inline">{t('common.delete', 'Delete')}</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {images.length === 0 && audios.length === 0 && (
              <div className="text-center py-8 sm:py-12 text-gray-500 px-4">
                <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">📁</div>
                <p className="text-sm sm:text-base md:text-lg font-medium mb-2">
                  {t('assessments.perform.noMedia', 'No media files')}
                </p>
                <p className="text-xs sm:text-sm text-gray-400 max-w-sm mx-auto">
                  {t(
                    'assessments.perform.noMediaDescription',
                    'Upload images or audio files to see them here'
                  )}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
